import React, { createContext, useCallback, useContext, useState } from 'react';
import sqfAiService from '../services/sqfAiService';

const API_URL = process.env.REACT_APP_API_URL || '';
const SqfImportContext = createContext(null);

export function SqfImportProvider({ children }) {
  const [state, setState] = useState({
    status: null,   // null | 'uploading' | 'processing' | 'done' | 'error'
    message: '',
    count: null,
    sqfCodePdfUrl: null,
    report: null,             // { created, updated, reactivated, deprecated, totalSections, skippedSections }
    applicabilityStats: null, // { applicable, not_applicable, conditional } — set when phase 2 ran
  });

  const startImport = useCallback(async (file, importMode = 'replace', facilityProfile = null) => {
    setState({ status: 'uploading', message: `Uploading ${file.name}…`, count: null, sqfCodePdfUrl: null, report: null, applicabilityStats: null });
    try {
      const { fileUrl, mimeType } = await sqfAiService.uploadFileForAi(file);

      setState(s => ({ ...s, status: 'processing', message: 'Starting AI analysis…' }));

      // Stream SSE events from the import-clauses endpoint
      const response = await fetch(`${API_URL}/api/sqf/ai/import-clauses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ fileUrl, mimeType, importMode, facilityProfile: facilityProfile || null }),
      });

      if (!response.ok || !response.body) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Import request failed.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamCompleted = false;

      const processBuffer = (flush) => {
        const parts = buffer.split('\n\n');
        // On flush (stream done), process all parts; otherwise hold the last incomplete one
        buffer = flush ? '' : parts.pop();
        for (const part of parts) {
          const line = part.split('\n').find(l => l.startsWith('data: '));
          if (!line) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'progress') {
              setState(s => ({ ...s, status: 'processing', message: data.message }));
            } else if (data.type === 'done') {
              streamCompleted = true;
              setState({ status: 'done', message: data.summary || `${data.count} clauses imported successfully.`, count: data.count, sqfCodePdfUrl: data.sqfCodePdfUrl, report: data.report || null, applicabilityStats: data.applicabilityStats || null });
            } else if (data.type === 'error') {
              streamCompleted = true;
              setState({ status: 'error', message: data.message, count: null, sqfCodePdfUrl: null, report: null });
            }
          } catch { /* ignore malformed event */ }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (value) buffer += decoder.decode(value, { stream: !done });
        processBuffer(done); // flush=true on final chunk so the last event isn't lost
        if (done) break;
      }

      // Stream ended without a done/error event — connection was likely dropped
      if (!streamCompleted) {
        setState({ status: 'error', message: 'Connection lost during import. The server may have timed out — please try again.', count: null, sqfCodePdfUrl: null, report: null, applicabilityStats: null });
      }
    } catch (err) {
      const msg = err.message || 'Import failed.';
      setState({ status: 'error', message: msg, count: null, sqfCodePdfUrl: null, report: null, applicabilityStats: null });
    }
  }, []);

  const dismiss = useCallback(() => {
    setState({ status: null, message: '', count: null, sqfCodePdfUrl: null, report: null, applicabilityStats: null });
  }, []);

  return (
    <SqfImportContext.Provider value={{ ...state, startImport, dismiss }}>
      {children}
    </SqfImportContext.Provider>
  );
}

export function useSqfImport() {
  return useContext(SqfImportContext);
}
